import React from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useWorkflowContext } from "../../Workflow/WorkflowContextProvider";

/**
 * NumberColumnSubMenu
 * Sub-menu component for number column type configuration
 * @param {Object} column - Column configuration object
 * @param {number} columnIndex - Index of the column
 * @param {Function} onColumnChange - Callback for column changes
 * @param {Array} usedWorkflowVariableIds - Array of already used workflow variable IDs
 * @param {string} elementId - ID of the table element
 */
const NumberColumnSubMenu = ({ column, columnIndex, onColumnChange, usedWorkflowVariableIds, elementId }) => {
  const { customWorkflowVariables } = useWorkflowContext();

  const showResultOperations = [
    { text: "Addition", value: "addition" },
    { text: "Average", value: "average" },
    { text: "Count", value: "count" },
  ];

  // Get available smart chips (number type only and not already used)
  const getAvailableSmartChips = () => {
    if (!customWorkflowVariables) return [];

    return customWorkflowVariables.filter((variable) => {
      // Only number type variables
      if (variable.type !== "number") return false;

      // Exclude already used workflow variables
      if (usedWorkflowVariableIds?.includes(variable.id)) return false;

      // Exclude variables already linked to other columns in this table
      const alreadyLinkedInTable = false; // This would need to be passed from parent

      return !alreadyLinkedInTable;
    });
  };

  const handleShowResultChange = (checked) => {
    const updatedColumn = {
      ...column,
      showResult: checked,
      showLinkDropdown: false,
      linkedSmartChipId: null,
    };
    onColumnChange(columnIndex, updatedColumn);
  };

  const handleOperationChange = (operation) => {
    const updatedColumn = {
      ...column,
      operation,
    };
    onColumnChange(columnIndex, updatedColumn);
  };

  const handleLinkDropdownChange = (checked) => {
    const updatedColumn = {
      ...column,
      showLinkDropdown: checked,
      linkedSmartChipId: checked ? column.linkedSmartChipId : null,
    };
    onColumnChange(columnIndex, updatedColumn);
  };

  const handleSmartChipChange = (chipId) => {
    const updatedColumn = {
      ...column,
      linkedSmartChipId: chipId,
    };
    onColumnChange(columnIndex, updatedColumn);
  };

  return (
    <div className='mt-2 bg-light'>
      <div className='mb-2'>
        <span className='form-label small'>Show Result</span>
      </div>
      <div className='d-flex align-items-center gap-2'>
        <input
          type='checkbox'
          id={`showResult_${column.id || columnIndex}`}
          checked={column.showResult || false}
          onChange={(e) => handleShowResultChange(e.target.checked)}
        />
        <div className='w-100'>
          <DropDownList
            data={showResultOperations}
            textField='text'
            dataItemKey='value'
            value={showResultOperations.find((option) => option.value === (column.operation || "addition"))}
            onChange={(e) => handleOperationChange(e.target.value.value)}
            className='w-100 m-0'
            disabled={!column.showResult}
          />
        </div>
      </div>

      {/* Link to Smart Chip Section */}
      <div className={`smart-chip-section-animated ${column.showResult ? "expanded" : "collapsed"}`}>
        <div className='mb-2'>
          <span className='form-label small'>Link to Smart Chip</span>
        </div>
        <div className='d-flex align-items-center gap-table-properties ml-1'>
          <input
            type='checkbox'
            id={`linkSmartChip_${column.id || columnIndex}`}
            checked={column.showLinkDropdown}
            onChange={(e) => handleLinkDropdownChange(e.target.checked)}
          />
          <div className='w-100'>
            <DropDownList
              data={getAvailableSmartChips()}
              textField='name'
              dataItemKey='id'
              value={column.linkedSmartChipId ? customWorkflowVariables?.find((chip) => chip.id === column.linkedSmartChipId) || null : null}
              onChange={(e) => handleSmartChipChange(e.target.value?.id || null)}
              className='w-100 m-0'
              placeholder='Select Smart Chip'
              disabled={!column.linkedSmartChipId && !column.showLinkDropdown}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NumberColumnSubMenu;
